import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import SeoHead from "@/components/SeoHead";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Section from "@/components/Section";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";

type State = "loading" | "ready" | "already" | "invalid" | "success" | "error";

const Unsubscribe = () => {
  const { t, i18n } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState<State>("loading");
  const [submitting, setSubmitting] = useState(false);

  const isSv = i18n.language?.startsWith("sv");
  const copy = isSv
    ? {
        title: "Avregistrera e-post",
        loading: "Kontrollerar din länk…",
        ready: "Klicka nedan för att bekräfta att du inte längre vill ta emot e-post från oss.",
        confirm: "Bekräfta avregistrering",
        already: "Du är redan avregistrerad.",
        invalid: "Länken är ogiltig eller har upphört att gälla.",
        success: "Klart — du kommer inte längre att få e-post från oss.",
        error: "Något gick fel. Försök igen om en stund.",
        home: "Till startsidan",
      }
    : {
        title: "Unsubscribe from emails",
        loading: "Checking your link…",
        ready: "Click below to confirm you no longer want to receive emails from us.",
        confirm: "Confirm unsubscribe",
        already: "You are already unsubscribed.",
        invalid: "This link is invalid or has expired.",
        success: "Done — you will no longer receive emails from us.",
        error: "Something went wrong. Please try again shortly.",
        home: "Back to home",
      };

  useEffect(() => {
    if (!token) {
      setState("invalid");
      return;
    }
    const validate = async () => {
      try {
        const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/handle-email-unsubscribe?token=${encodeURIComponent(token)}`;
        const res = await fetch(url, {
          headers: { apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY },
        });
        const data = await res.json();
        if (res.ok && data.valid === true) setState("ready");
        else if (data.reason === "already_unsubscribed") setState("already");
        else setState("invalid");
      } catch {
        setState("error");
      }
    };
    validate();
  }, [token]);

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke("handle-email-unsubscribe", {
        body: { token },
      });
      if (error) {
        setState("error");
      } else if (data?.success) {
        setState("success");
      } else if (data?.reason === "already_unsubscribed") {
        setState("already");
      } else {
        setState("error");
      }
    } catch {
      setState("error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SeoHead
        title={{ en: "Unsubscribe | AI-labbet", sv: "Avregistrera | AI-labbet" }}
        description={{ en: "Manage your email preferences.", sv: "Hantera dina e-postinställningar." }}
        path="/unsubscribe"
        noIndex
      />
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-grow">
          <Section title={copy.title} titleAs="h1" containerSize="narrow">
            <div className="max-w-md mx-auto bg-card border border-border rounded-lg p-8 text-center space-y-6">
              {state === "loading" && <p className="text-muted-foreground">{copy.loading}</p>}
              {state === "ready" && (
                <>
                  <p className="text-foreground">{copy.ready}</p>
                  <Button onClick={handleConfirm} disabled={submitting} variant="primary" size="lg" className="w-full">
                    {copy.confirm}
                  </Button>
                </>
              )}
              {state === "already" && <p className="text-foreground">{copy.already}</p>}
              {state === "invalid" && <p className="text-foreground">{copy.invalid}</p>}
              {state === "success" && <p className="text-foreground">{copy.success}</p>}
              {state === "error" && <p className="text-destructive">{copy.error}</p>}
              {(state === "already" || state === "success" || state === "invalid") && (
                <Link to={isSv ? "/sv" : "/"} className="text-sm text-muted-foreground underline">
                  {copy.home}
                </Link>
              )}
            </div>
          </Section>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default Unsubscribe;
